import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import PrimaryButton from "../../../Components/Buttons/primary-button";

import renderStatusModal from "../status-modal";

import { useModalContext } from "../../../Contexts/ModalContext";

import { useUserAccount } from "../../../CustomHooks/Queries";
import { useInitiateVenmoTransfer } from "../../../CustomHooks/Mutations";

import { formatCurrency, transformCountryCodeFormatToUSNumber } from "../../../Utils/helpers";
import { modalVariants, SuccessfulTransferStatusModalProps } from "../../../Utils/constants";

import { VenmoLogo } from "../../../Assets/Images";
import { CircleExclamationOutlined, CloseWithShadow } from "../../../Assets/Icons";

export default function renderAuthorizeVenmoTransfer(modalContext, props) {
  const { updateModalComponent, pushModalStack, setModalVariant, setModalTitle, setModalClassName } = modalContext;

  updateModalComponent(() => <AuthorizeVenmoTransfer {...props} />);
  pushModalStack(() => renderAuthorizeVenmoTransfer(modalContext, props));
  setModalVariant(modalVariants.flexDialog);
  setModalClassName("has-transfer-info full-modal-width hide-back-button");
  setModalTitle("Confirm your payment");
}

const AuthorizeVenmoTransfer = ({ cashAmount }) => {
  const navigate = useNavigate();

  const [agreed, setAgreed] = useState(false);

  const modalContext = useModalContext();
  const { popModalStack } = modalContext;

  const { data: userAccountData } = useUserAccount();

  const phoneNumber = useMemo(
    () => transformCountryCodeFormatToUSNumber(userAccountData?.data?.data?.user?.phoneNumber ?? ""),
    [userAccountData]
  );

  const onWithDrawSuccess = ({ data }) => {
    const GoToPreviousPage = () => navigate(-1);
    modalContext.updateModalOnCloseCallBack(GoToPreviousPage);
    renderStatusModal(modalContext, {
      ...SuccessfulTransferStatusModalProps,
      description: data?.data,
      onCTAButtonClick: () => modalContext.onModalClose({ callOnClose: GoToPreviousPage }),
    });
  };

  const onWithDrawError = (errorProps = {}) =>
    renderStatusModal(modalContext, {
      StatusIcon: CloseWithShadow,
      statusClassName: "error",
      title: "We're having trouble completing your transfer.",
      description: "Try again in a few minutes. Contact Stake if the problem persists.",
      ...errorProps,
      CTAButtonText: "Retry",
      onCTAButtonClick: () => popModalStack(0),
      secondaryCTAButtonText: "Cancel",
      onSecondaryCTAButtonClick: () => modalContext.onModalClose(),
    });

  const { isLoading: isInitiatingVenmoTransfer, mutate: InitiateVenmoTransfer } = useInitiateVenmoTransfer({
    onSuccess: onWithDrawSuccess,
    onError: (err) => onWithDrawError(err?.response?.status === 422 ? { description: err?.response?.data?.error } : {}),
  });

  const onSubmit = () => InitiateVenmoTransfer(cashAmount);

  return (
    <>
      <div className="modal-dialog-content">
        <div className="payee-card">
          <div className="account-badge">
            <VenmoLogo className="venmo-logo filled" />
          </div>
          <div className="info-container">
            <p className="title">Venmo</p>
            <p className="description">{phoneNumber}</p>
          </div>
        </div>
        <div className="payment-details mb-4">
          <div className="transfer-detail">
            <span className="detail-label">Amount</span>
            <span className="detail-value">{formatCurrency(cashAmount)}</span>
          </div>
          <div className="transfer-detail">
            <span className="detail-label">Fee</span>
            <span className="detail-value">Free</span>
          </div>
          <div className="transfer-detail">
            <span className="detail-label">From</span>
            <span className="detail-value">Stake Wallet</span>
          </div>
        </div>
        <div className="info-alert error">
          <CircleExclamationOutlined />
          If your Venmo account uses a different phone number, please do not proceed with this transfer.
        </div>
        <label className="ach-authorization-checkbox">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span className="ach-authorization-text">
            By tapping Authorize, you instruct Stake to send {formatCurrency(cashAmount)} to the Venmo account linked to{" "}
            {phoneNumber}. Instant transfers are final and can't be canceled once sent.
          </span>
        </label>
      </div>
      <div className="action-container split-equally">
        <PrimaryButton onClick={onSubmit} disabled={isInitiatingVenmoTransfer || !agreed}>
          Authorize {isInitiatingVenmoTransfer && <CircularProgress />}
        </PrimaryButton>
        <PrimaryButton className="secondary-colors" onClick={() => popModalStack(1)}>
          Cancel
        </PrimaryButton>
      </div>
    </>
  );
};
